import { useState } from "react";
import { Head, Link, useForm, router } from "@inertiajs/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { CATEGORY_OPTIONS } from "@/lib/constants";

const STATUS_LABELS = {
  validated: "Validé",
  ready_for_analysis: "Prêt pour analyse",
  analyzing: "En cours d'analyse",
  analysis_error: "Erreur d'analyse",
  pending: "En attente d'informations",
};

const STATUS_VARIANTS = {
  validated: "default",
  ready_for_analysis: "secondary",
  analyzing: "outline",
  analysis_error: "destructive",
  pending: "outline",
};

export default function ProductEdit({ product }) {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { data, setData, patch, processing, errors } = useForm({
    brand: product.brand || "",
    name: product.name || "",
    category: product.category || "cat_kibble",
    ingredients: product.ingredients || "",
  });

  function handleSubmit(e) {
    e.preventDefault();
    patch(`/admin/products/${product.id}`, {
      preserveScroll: true,
      onSuccess: ({ props: { product } }) => {
        console.log(product);
        toast.success("Produit enregistré");
      },
    });
  }

  function handleAnalyze() {
    setIsAnalyzing(true);
    router.post(
      `/admin/products/${product.id}/analyze`,
      {},
      {
        preserveScroll: true,
        onFinish: () => setIsAnalyzing(false),
        onSuccess: () => toast.success("Analyse lancée"),
      },
    );
  }

  function handleDelete() {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) {
      router.delete(`/admin/products/${product.id}`, {
        onSuccess: () => toast.success("Produit supprimé"),
      });
    }
  }

  return (
    <div className="p-6 max-w-4xl">
      <Head title={`Modifier ${product.name || product.ean}`} />

      <div className="flex items-center gap-4 mb-6">
        <Link href={`/admin/products?status=${product.status}`} className="text-blue-600 hover:underline">
          ← Retour
        </Link>
        <h1 className="text-2xl font-bold">Modifier le produit</h1>
        <Button variant="destructive" size="sm" className="ml-auto" onClick={handleDelete}>
          Supprimer
        </Button>
      </div>

      <div className="grid gap-6">
        {product.analysis_error && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-700">Erreur d'analyse</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-red-100 p-4 rounded-lg overflow-auto text-sm text-red-800 whitespace-pre-wrap">
                {product.analysis_error}
              </pre>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Informations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-6 mb-6">
              <div className="w-32 h-32 rounded-lg border bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                {product.image ? (
                  <img
                    src={product.image}
                    alt={product.name || "Photo du produit"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-gray-400 text-sm text-center px-2">Aucune image</span>
                )}
              </div>
              <div>
                <span className="text-sm text-gray-500">Code-barres (EAN)</span>
                <p className="font-mono text-lg">{product.ean}</p>
                <img
                  src={`/products/${product.ean}/barcode`}
                  alt={`Code-barres ${product.ean}`}
                  className="mt-2 h-20"
                />
              </div>
              <div>
                <span className="text-sm text-gray-500">Statut</span>
                <div className="mt-1 flex flex-col gap-2">
                  <Badge variant={STATUS_VARIANTS[product.status]}>{STATUS_LABELS[product.status]}</Badge>
                  {(product.status === "ready_for_analysis" || product.status === "analysis_error") && (
                    <Button type="button" size="sm" variant="outline" onClick={handleAnalyze} disabled={isAnalyzing}>
                      {isAnalyzing ? "Lancement..." : "Lancer l'analyse"}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 flex flex-col">
              <div className="space-y-2">
                <Label htmlFor="brand">Marque</Label>
                <Input
                  id="brand"
                  value={data.brand}
                  onChange={(e) => setData("brand", e.target.value)}
                  placeholder="Marque du produit"
                />
                {errors.brand && <p className="text-sm text-red-500">{errors.brand}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Nom</Label>
                <Input
                  id="name"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  placeholder="Nom du produit"
                />
                {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Catégorie</Label>
                <select
                  id="category"
                  value={data.category}
                  onChange={(e) => setData("category", e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.category && <p className="text-sm text-red-500">{errors.category}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="ingredients">Ingrédients</Label>
                <Textarea
                  id="ingredients"
                  value={data.ingredients}
                  onChange={(e) => setData("ingredients", e.target.value)}
                  placeholder="Liste des ingrédients..."
                  rows={6}
                />
                {errors.ingredients && <p className="text-sm text-red-500">{errors.ingredients}</p>}
              </div>

              <Button className="ml-auto" type="submit" disabled={processing}>
                {processing ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {product.photos.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Photos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 flex-wrap">
                {product.photos.map((photo) => (
                  <img
                    key={photo.id}
                    src={photo.url}
                    alt="Photo du produit"
                    className="w-48 h-48 object-cover rounded-lg border cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setSelectedPhoto(photo)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
          <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden">
            {selectedPhoto && <img src={selectedPhoto.url} className="w-full h-full max-h-[90vh] object-contain" />}
          </DialogContent>
        </Dialog>

        {product.analysis && (
          <Card>
            <CardHeader>
              <CardTitle>Analyse IA</CardTitle>
            </CardHeader>
            <CardContent>
              <pre className="bg-gray-100 p-4 rounded-lg overflow-auto text-sm">
                {JSON.stringify(product.analysis, null, 2)}
              </pre>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
